import { useEffect, useMemo, useState, useRef } from "react";
import { Alert, Card, Pagination, Progress, Spin, Tag } from "antd";

import GoogleMapReact, { fitBounds } from 'google-map-react';
import { StarOutlined, AimOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { DOT_COLOR, MarkerGeoGrid } from "./MarkerGeoGrid";
import { GeogridPointPanel } from "../_drawers/GeogridPointDrawer";
import { EmptyStateBox } from "../../../../components/design/EmptyStateBox/EmptyStateBox";
import { PageInfoCard } from "../../../common/PageInfoCard";
import {
    calcVisibilityRadius,
    getDistanceProximity,
    getLocalpackPresenceMetrics,
    getVisibilityRadiusBadgeStyle,
    getVisibilityRadiusColor,
    highlightKeywordMatches,
    KeywordMatchIcon,
    LocalpackMetricsCard,
    OpenStatusBadge,
} from "./RankingCardUtils";
import "../../../common/PageTitle.css";

const MI_NEGOCIO_INFO = `En esta vista puedes analizar cómo aparece tu negocio en distintas coordenadas del área de estudio.

- Puntos verdes / total: número de coordenadas donde estás en el localpack (posiciones 1-3) respecto al total del grid.
- **Presencia Localpack**: porcentaje de puntos del grid en los que tu negocio está entre las 3 primeras posiciones.
- **Radio de visibilidad**: estimación del alcance en metros: radio del grid × 1.2 × √(puntos en localpack) / √(total de puntos).`;

export const GeogridVisibilityMap = (props: { geogrid: any, keyword?: string }) => {
    const [zoom, setZoom] = useState(10);
    const [center, setCenter] = useState<any>(null);
    const [selectedPoint, setSelectedPoint] = useState<any>(null);
    const [infoExpanded, setInfoExpanded] = useState(false);

    const localpackMetrics = useMemo(
        () => getLocalpackPresenceMetrics(props.geogrid, props.geogrid?.points),
        [props.geogrid]
    );

    useEffect(() => {
        const points = props.geogrid?.points;
        if (!points?.length) return;

        let minLat = points[0]?.lat;
        let maxLat = points[0]?.lat;
        let minLng = points[0]?.lng;
        let maxLng = points[0]?.lng;

        points.forEach((marker: { lat: number; lng: number; }) => {
            minLat = Math.min(minLat, marker.lat);
            maxLat = Math.max(maxLat, marker.lat);
            minLng = Math.min(minLng, marker.lng);
            maxLng = Math.max(maxLng, marker.lng);
        });

        const neswBounds: any = {
            nw: { lat: minLat, lng: minLng },
            se: { lat: maxLat, lng: maxLng }
        };

        const { center, zoom } = fitBounds(neswBounds, { width: 500, height: 500 });
        setCenter(center);
        setZoom(zoom);
        setSelectedPoint(null);
    }, [props.geogrid?.points]);

    return (
        <div style={{ display: 'flex', gap: 16, color: 'slategray', minHeight: '70vh' }}>
            <div style={{ flex: '1 1 50%', minWidth: 0 }}>
                <div style={{ width: '100%', marginBottom: 10 }}>
                    <LocalpackMetricsCard
                        showPoints
                        green={localpackMetrics.green}
                        total={localpackMetrics.total}
                        presencePercent={localpackMetrics.presencePercent}
                        visibilityRadius={localpackMetrics.visibilityRadius}
                        percentColor={localpackMetrics.percentColor}
                        trailing={
                            <button
                                type="button"
                                className="page-info-icon"
                                aria-label={infoExpanded ? 'Ocultar información' : 'Mostrar información'}
                                aria-expanded={infoExpanded}
                                onClick={() => setInfoExpanded((prev) => !prev)}
                                style={{ flexShrink: 0, marginLeft: 'auto' }}
                            >
                                {infoExpanded ? <UpOutlined /> : <DownOutlined />}
                            </button>
                        }
                    />
                    {infoExpanded && (
                        <div style={{ marginTop: 12 }}>
                            <PageInfoCard
                                title="Info"
                                description={MI_NEGOCIO_INFO}
                            />
                        </div>
                    )}
                </div>
                <div style={{ width: '100%', height: '65vh', overflow: 'hidden', position: 'relative' }}>
                    {center ?
                        <GoogleMapReact
                            bootstrapURLKeys={{ key: "AIzaSyCtmjrbwFhUPNP-LfETh_aAKaFuZJhwDm4" }}
                            defaultCenter={{ lat: 0, lng: 0 }}
                            center={center}
                            defaultZoom={10}
                            zoom={zoom}
                            yesIWantToUseGoogleMapApiInternals>
                            {props.geogrid.points.map((point: any, index: any) => (
                                <MarkerGeoGrid
                                    key={index}
                                    lat={point.lat}
                                    lng={point.lng}
                                    point={point}
                                    onPointSelect={setSelectedPoint}
                                    selected={!!selectedPoint
                                        && selectedPoint.lat === point.lat
                                        && selectedPoint.lng === point.lng}
                                />
                            ))}
                        </GoogleMapReact>
                        :
                        <Spin />
                    }
                </div>
            </div>
            <div style={{ flex: '1 1 50%', minWidth: 0, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
                {selectedPoint ? (
                    <GeogridPointPanel point={selectedPoint} keyword={props.keyword} />
                ) : (
                    <div style={{
                        height: '100%',
                        minHeight: 280,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 24,
                        overflowY: 'auto',
                    }}>
                        <EmptyStateBox
                            icon={<AimOutlined />}
                            title="Selecciona una coordenada"
                            description="Haz clic en un punto del mapa para ver los negocios de esa ubicación"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export const CompetitorsRankingPanel = (props: { geogrid: any, hideTitle?: boolean }) => {
    const gridRadius = props.geogrid?.radius ?? 0;
    const placesStats = props.geogrid?.allPlacesStats || [];
    const keyword = props.geogrid?.text || props.geogrid?.keyword || '';
    const pageSize = 20;

    const ownIndex = useMemo(
        () => placesStats.findIndex((place: any) => place.reference),
        [placesStats]
    );

    const [page, setPage] = useState(() =>
        ownIndex >= 0 ? Math.floor(ownIndex / pageSize) + 1 : 1
    );
    const listRef = useRef<HTMLDivElement>(null);

    const pageItems = useMemo(() => {
        const start = (page - 1) * pageSize;
        return placesStats.slice(start, start + pageSize);
    }, [placesStats, page]);

    useEffect(() => {
        if (ownIndex >= 0) {
            setPage(Math.floor(ownIndex / pageSize) + 1);
        } else {
            setPage(1);
        }
    }, [ownIndex, props.geogrid]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            if (ownIndex >= 0) {
                document
                    .querySelector('.competitors-ranking-cards .own-business-card')
                    ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            } else if (listRef.current) {
                listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }, 80);
        return () => window.clearTimeout(timer);
    }, [page, ownIndex, placesStats]);

    return (
        <div ref={listRef} style={{ color: 'slategray', overflowY: 'auto', maxHeight: '70vh' }}>
            {placesStats.length > 0 ?
                <>
                    {!props.hideTitle && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ marginLeft: '10px', color: 'darkslategrey' }}><b>Top competidores con mejor ranking</b></h3>
                        </div>
                    )}
                    <div className="competitors-ranking-cards" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 10px 12px' }}>
                        {pageItems.map((record: any) => {
                            const isOwn = !!record.reference;
                            const color = DOT_COLOR[record.rankPosition] || 'silver';
                            const percent = Math.round(100 * (record.localPackPercent ?? 0));
                            const meters = record.distanceToPoint ?? record.localPackMaxDistance ?? 0;
                            const visibilityRadius = calcVisibilityRadius(
                                gridRadius,
                                null,
                                null,
                                record.localPackPercent ?? 0,
                            );
                            const percentColor = getVisibilityRadiusColor(visibilityRadius);
                            const proximity = meters > 0 ? getDistanceProximity(Number(meters)) : null;

                            return (
                                <Card
                                    key={record.rankPosition}
                                    size="small"
                                    className={isOwn ? 'own-business-card' : undefined}
                                    style={{
                                        borderRadius: '0 10px 10px 0',
                                        border: isOwn ? `1.5px solid color-mix(in srgb, ${color} 55%, #94a3b8)` : '1.5px solid #cbd5e1',
                                        background: isOwn ? `color-mix(in srgb, ${color} 12%, white)` : '#fff',
                                        boxShadow: isOwn ? `inset 4px 0 0 ${color}` : 'none',
                                    }}
                                    bodyStyle={{ padding: '12px 14px' }}
                                >
                                    <div style={{ display: 'flex', gap: 14, alignItems: 'stretch' }}>
                                        <div style={{
                                            backgroundColor: isOwn ? color : '#e2e8f0',
                                            color: isOwn ? 'white' : '#64748b',
                                            height: 30,
                                            width: 30,
                                            borderRadius: '50%',
                                            border: isOwn ? 'solid 2px white' : 'solid 1px #cbd5e1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: isOwn ? 700 : 500,
                                            flexShrink: 0,
                                            marginTop: 2,
                                        }}>
                                            {record.rankPosition}
                                        </div>

                                        <div style={{ flex: '1 1 42%', minWidth: 0 }}>
                                            <div style={{ marginBottom: 10 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                    {isOwn ? (
                                                        <Tag style={{ color: 'blue', borderRadius: 20, margin: 0 }} color="dodgerblue">
                                                            <a href={record.googleMapsUri} target="blank">
                                                                {record.nameContainsKeyword
                                                                    ? highlightKeywordMatches(record.name, keyword)
                                                                    : record.name}
                                                            </a>
                                                        </Tag>
                                                    ) : (
                                                        <a href={record.googleMapsUri} target="blank" style={{ fontWeight: 600, color: '#334155' }}>
                                                            {record.nameContainsKeyword
                                                                ? highlightKeywordMatches(record.name, keyword)
                                                                : record.name}
                                                        </a>
                                                    )}
                                                    {record.nameContainsKeyword && (
                                                        <KeywordMatchIcon fieldLabel="Nombre" keyword={keyword} />
                                                    )}
                                                </div>
                                                {record.category && (
                                                    <div style={{
                                                        marginTop: 4,
                                                        fontSize: 12,
                                                        color: '#475569',
                                                        fontWeight: 400,
                                                        lineHeight: 1.3,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        flexWrap: 'wrap',
                                                    }}>
                                                        <span style={{ fontWeight: 400 }}>
                                                            {record.categoryContainsKeyword
                                                                ? highlightKeywordMatches(record.category, keyword, false)
                                                                : record.category}
                                                        </span>
                                                        {record.categoryContainsKeyword && (
                                                            <KeywordMatchIcon fieldLabel="Categoría" keyword={keyword} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{
                                                padding: '10px 12px',
                                                borderRadius: 10,
                                                background: '#f1f5f9',
                                                border: '1px solid #e2e8f0',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 10,
                                            }}>
                                                <div>
                                                    <div style={{ fontSize: 10, color: '#475569', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: 2 }}>
                                                        Radio de visibilidad
                                                    </div>
                                                    <span style={getVisibilityRadiusBadgeStyle(percentColor)}>
                                                        {visibilityRadius} mts.
                                                    </span>
                                                </div>

                                                <div style={{ lineHeight: 1 }}>
                                                    <div style={{ fontSize: 10, color: '#475569', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: 4 }}>
                                                        Presencia Localpack
                                                    </div>
                                                    <Progress
                                                        size="small"
                                                        percent={percent}
                                                        showInfo
                                                        strokeColor="LimeGreen"
                                                        trailColor="#e2e8f0"
                                                        strokeWidth={7}
                                                        format={(p) => (
                                                            <span style={{ color: '#334155', fontWeight: isOwn ? 700 : 600, fontSize: 13 }}>
                                                                {p}%
                                                            </span>
                                                        )}
                                                        style={{ margin: 0, maxWidth: 220, lineHeight: 1 }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{
                                            flex: '1.35 1 48%',
                                            minWidth: 180,
                                            borderLeft: '1px solid #e2e8f0',
                                            paddingLeft: 14,
                                        }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                <div>
                                                    <div style={{ fontSize: 10, color: '#0f172a', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: 6 }}>
                                                        Geolocalización
                                                    </div>
                                                    <div style={{
                                                        paddingLeft: 8,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        flexWrap: 'wrap',
                                                    }}>
                                                        <span style={{ color: '#64748b', fontWeight: 500, fontSize: 13 }}>
                                                            {isOwn
                                                                ? (meters > 0
                                                                    ? <>Alcance <b>{Number(meters).toFixed(0)} mts.</b></>
                                                                    : <span style={{ fontWeight: 400 }}>Tu negocio</span>)
                                                                : (meters > 0
                                                                    ? <>a <b>{Number(meters).toFixed(0)} mts.</b> de tu negocio</>
                                                                    : <span style={{ fontWeight: 400 }}>Sin distancia</span>)}
                                                        </span>
                                                        {proximity && (
                                                            <Tag
                                                                style={{
                                                                    margin: 0,
                                                                    borderRadius: 6,
                                                                    fontWeight: 600,
                                                                    fontSize: 11,
                                                                    lineHeight: '18px',
                                                                    border: '1px solid #94a3b8',
                                                                    color: '#94a3b8',
                                                                    background: '#fff',
                                                                }}
                                                            >
                                                                {proximity}
                                                            </Tag>
                                                        )}
                                                    </div>
                                                </div>

                                                <div style={{ paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
                                                    <div style={{ fontSize: 10, color: '#0f172a', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: 6 }}>
                                                        Negocio
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 8 }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, alignItems: 'center', fontSize: 13 }}>
                                                            <span style={{ color: '#64748b' }}>Reseñas</span>
                                                            <span style={{ color: '#334155', fontWeight: 500 }}>
                                                                {record.rating > 0 ? (
                                                                    <>
                                                                        <StarOutlined style={{ marginRight: 4 }} />
                                                                        {Math.round(10 * record.rating) / 10}
                                                                        {record.userRatingsTotal > 0 && (
                                                                            <span style={{ color: '#94a3b8', fontWeight: 400 }}> · {record.userRatingsTotal}</span>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <span style={{ color: '#94a3b8' }}>Sin reseñas</span>
                                                                )}
                                                            </span>
                                                        </div>

                                                        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, alignItems: 'center', fontSize: 13 }}>
                                                            <span style={{ color: '#64748b' }}>Horario</span>
                                                            <OpenStatusBadge record={record} />
                                                        </div>

                                                        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, alignItems: 'center', fontSize: 13 }}>
                                                            <span style={{ color: '#64748b' }}>Web</span>
                                                            <span style={{ color: '#334155', fontWeight: 500 }}>
                                                                {(record.hasWebsite ?? !!record.websiteUri) ? (
                                                                    record.websiteUri ? (
                                                                        <a href={record.websiteUri} target="blank">Tiene web</a>
                                                                    ) : (
                                                                        'Tiene web'
                                                                    )
                                                                ) : (
                                                                    <span style={{ color: '#94a3b8' }}>Sin web</span>
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}

                        {placesStats.length > pageSize && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
                                <Pagination
                                    size="small"
                                    current={page}
                                    pageSize={pageSize}
                                    total={placesStats.length}
                                    onChange={setPage}
                                    showSizeChanger={false}
                                />
                            </div>
                        )}
                    </div>
                </>
                :
                <>
                    {!props.hideTitle && (
                        <h3 style={{ marginLeft: '10px', color: 'darkslategrey' }}><b>Top competidores con mejor ranking</b></h3>
                    )}
                    <Alert type="warning" showIcon message="No hay competidores en esta búsqueda, puede ser debido a un único negocio que monipoliza la SERP" />
                </>
            }
        </div>
    );
};

/** @deprecated Prefer tabbed Informe / Datos panels */
export const GeogridAndListPanel = (props: { geogrid: any }) => {
    return (
        <div style={{ display: 'flex', color: 'slategray', justifyContent: 'space-between' }}>
            <div style={{ width: '600px' }}>
                <GeogridVisibilityMap geogrid={props.geogrid} />
            </div>
            <div style={{ margin: '0px 20px', flex: 1 }}>
                <CompetitorsRankingPanel geogrid={props.geogrid} />
            </div>
        </div>
    );
};

